import { Alert, Button, Card, Form, Input, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationRegisterSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type AmendmentRow = {
  id: string;
  register_id: string;
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  attendance_date?: string;
  section_name?: string;
  original_status: string;
  requested_status: string;
  reason: string;
  status: string;
};

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
  { value: 'excused', label: 'Excused' },
];

function asList<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

export default function AttendanceCorrectionsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const amendments = useQuery({
    queryKey: ['education-attendance-amendments'],
    queryFn: async () => asList<AmendmentRow>((await api.get('/education/attendance/amendments')).data),
  });

  const onError = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));

  const request = useMutation({
    mutationFn: (values: Record<string, string>) => api.post('/education/attendance/amendments', values),
    onSuccess: () => {
      message.success(t('correctionRequested', { defaultValue: 'Correction submitted for approval' }));
      queryClient.invalidateQueries({ queryKey: ['education-attendance-amendments'] });
    },
    onError,
  });

  return (
    <EducationPageChrome
      title={t('attendanceCorrections')}
      description={t('attendanceCorrectionsDetailIntro', {
        defaultValue:
          'Corrections keep the submitted mark and request a new status through approval. Submit the daily register before requesting a correction.',
      })}
      tags={<Tag>{(amendments.data ?? []).length}</Tag>}
      extra={<Button onClick={() => navigate('/education/attendance')}>{t('backToAttendance')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={t('correctionQueueHint', {
            defaultValue: 'Pending corrections appear below. Approvals run through the school attendance amendment workflow.',
          })}
        />
        <Card size="small" title={t('correctionQueue', { defaultValue: 'Correction queue' })}>
          <Table
            rowKey="id"
            size="small"
            loading={amendments.isLoading}
            dataSource={amendments.data ?? []}
            pagination={{ pageSize: 15 }}
            columns={[
              { title: t('attendanceDate'), dataIndex: 'attendance_date', width: 120 },
              { title: t('section'), dataIndex: 'section_name', width: 120 },
              {
                title: t('student'),
                key: 'student',
                render: (_: unknown, row: AmendmentRow) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`.trim() || '—',
              },
              { title: t('originalStatus'), dataIndex: 'original_status', width: 120 },
              { title: t('requestedStatus'), dataIndex: 'requested_status', width: 120 },
              { title: t('reason'), dataIndex: 'reason', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (status: string) => <Tag color={status === 'pending_approval' ? 'orange' : undefined}>{status}</Tag>,
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('requestCorrection', { defaultValue: 'Request correction' })}>
          <Form layout="vertical" onFinish={(values) => request.mutate(values)} style={{ maxWidth: 520 }}>
            <Form.Item name="register_id" label={t('register')} rules={[{ required: true }]}>
              <EducationRegisterSelect />
            </Form.Item>
            <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
              <EducationStudentSelect />
            </Form.Item>
            <Form.Item name="requested_status" label={t('requestedStatus')} rules={[{ required: true }]}>
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
            <Form.Item name="reason" label={t('reason')} rules={[{ required: true }]}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={request.isPending}>
              {t('save')}
            </Button>
          </Form>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
